import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
  Store,
} from '@ngxs/store';
import { captureException } from '@sentry/capacitor';
import { WorkTimeUtilizationsGatewayService } from '@wilson/api/gateway';
import { AuthState } from '@wilson/auth/core';
import { UserKpisInMinutes, WorktimeUtilizationDTO } from '@wilson/interfaces';
import { ShiftTimelinePreferredSettingsState } from '@wilson/preferred-settings/state';
import {
  endOfMonth,
  endOfWeek,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  catchError,
  debounceTime,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { TimelineKpisStateModel } from '../interfaces';
import { ShiftTimelineDataState } from '../shift-timeline-data/shift-timeline-data.state';
import { defaultTimelineKpisState } from '../shift-timeline-default-data';
import {
  FetchKpisForVisibleUsers,
  SubscribeToKpiRequests,
  UnsubscribeFromKpiRequests,
  UpdateKpisOfUsers,
} from './timeline-kpis.actions';

const DATABASE_DATE_TIME_FORMAT = "yyyy-MM-dd'T'00:00:00.000'Z'";

@State({
  name: 'timelineKpisState',
  defaults: defaultTimelineKpisState,
})
@Injectable()
export class TimelineKpisState {
  private readonly store = inject(Store);
  private readonly workTimeUtilizationsGatewayService = inject(
    WorkTimeUtilizationsGatewayService,
  );
  private readonly nzMessageService = inject(NzMessageService);
  private readonly translateService = inject(TranslateService);

  private weekKpiFetchRequests$ = new Subject<{
    userIds: string[];
    timeFrame: { startDatetime: string; endDatetime: string };
    ctx: StateContext<TimelineKpisStateModel>;
  }>();
  private monthKpiFetchRequests$ = new Subject<{
    userIds: string[];
    timeFrame: { startDatetime: string; endDatetime: string };
    ctx: StateContext<TimelineKpisStateModel>;
  }>();
  private weekKpiUpdateRequests$ = new Subject<{
    userIds: string[];
    timeFrame: { startDatetime: string; endDatetime: string };
    ctx: StateContext<TimelineKpisStateModel>;
  }>();
  private monthKpiUpdateRequests$ = new Subject<{
    userIds: string[];
    timeFrame: { startDatetime: string; endDatetime: string };
    ctx: StateContext<TimelineKpisStateModel>;
  }>();

  private weekKpiFetchRequestsSubscription!: Subscription;
  private monthKpiFetchRequestsSubscription!: Subscription;
  private weekKpiUpdateRequestsSubscription!: Subscription;
  private monthKpiUpdateRequestsSubscription!: Subscription;
  private readonly WEEK_CHUNK_SIZE = 15; // These values are intentionally separated to allow for individually chunking sizes
  private readonly MONTH_CHUNK_SIZE = 10; // These values are intentionally separated to allow for individually chunking sizes
  private userIdsWithWeekDataLoaded: string[] = [];
  private userIdsWithMonthDataLoaded: string[] = [];

  private previousWeekKpiFetchParameters: {
    userIds: string[];
    timeFrame: { startDatetime: string; endDatetime: string };
  } | null = null;
  private previousMonthKpiFetchParameters: {
    userIds: string[];
    timeFrame: { startDatetime: string; endDatetime: string };
  } | null = null;

  @Selector()
  static userKpisForWeek(
    state: TimelineKpisStateModel,
  ): WorktimeUtilizationDTO {
    return state.userKpisForWeek;
  }

  @Selector()
  static userKpisForMonth(
    state: TimelineKpisStateModel,
  ): WorktimeUtilizationDTO {
    return state.userKpisForMonth;
  }

  @Selector()
  static fetchingWeeklyKpiForUserIds(state: TimelineKpisStateModel): string[] {
    return state.fetchingWeeklyKpiForUserIds;
  }

  @Selector()
  static fetchingMonthlyKpiForUserIds(state: TimelineKpisStateModel): string[] {
    return state.fetchingMonthlyKpiForUserIds;
  }

  @Selector()
  static updatingKpiUserIds(state: TimelineKpisStateModel): string[] {
    return state.updatingKpiUserIds;
  }

  static userKpisForWeekByUserId(
    userId: string,
  ): (userKpisForWeek: WorktimeUtilizationDTO) => UserKpisInMinutes {
    return createSelector(
      [TimelineKpisState.userKpisForWeek],
      (userKpisForWeek: WorktimeUtilizationDTO) => {
        return userKpisForWeek[userId];
      },
    );
  }

  static userKpisForMonthByUserId(
    userId: string,
  ): (userKpisForMonth: WorktimeUtilizationDTO) => UserKpisInMinutes {
    return createSelector(
      [TimelineKpisState.userKpisForMonth],
      (userKpisForMonth: WorktimeUtilizationDTO) => {
        return userKpisForMonth[userId];
      },
    );
  }

  private splitIntoChunks(array: string[], chunkSize: number): string[][] {
    const chunks: string[][] = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }

  private processChunksSequentially(
    chunks: string[][],
    processChunk: (chunk: string[]) => Observable<WorktimeUtilizationDTO>,
    onChunkComplete: (result: WorktimeUtilizationDTO) => void,
  ): Observable<void> {
    return new Observable<void>((subscriber) => {
      let currentIndex = 0;

      const processNextChunk = (): void => {
        if (currentIndex >= chunks.length || subscriber.closed) {
          subscriber.complete();
        } else {
          const chunk = chunks[currentIndex];
          const processChunkObservable = processChunk(chunk)
            .pipe(
              tap(() => {
                if (subscriber.closed) {
                  processChunkObservable.unsubscribe();
                }
              }),
              catchError((error) => {
                console.error('Error processing chunk:', error);
                captureException(error);
                this.nzMessageService.error(
                  this.translateService.instant('general.error'),
                );
                return of(null);
              }),
            )
            .subscribe({
              next: (result) => {
                if (!subscriber.closed) {
                  if (result) {
                    onChunkComplete(result);
                  }
                  currentIndex++;
                  processNextChunk();
                }
              },
              error: (error) => {
                if (!subscriber.closed) {
                  captureException(error);
                  currentIndex++;
                  processNextChunk();
                }
              },
            });
        }
      };

      processNextChunk();
    });
  }

  @Action(SubscribeToKpiRequests)
  subscribeToKpiRequests(): void {
    this.handleWeekKpiFetchRequests();
    this.handleMonthKpiFetchRequests();
    this.handleWeekKpiUpdateRequests();
    this.handleMonthKpiUpdateRequests();
  }

  @Action(UnsubscribeFromKpiRequests)
  unsubscribeFromKpiRequests(): void {
    this.weekKpiFetchRequestsSubscription?.unsubscribe();
    this.monthKpiFetchRequestsSubscription?.unsubscribe();
    this.weekKpiUpdateRequestsSubscription?.unsubscribe();
    this.monthKpiUpdateRequestsSubscription?.unsubscribe();
  }

  @Action(FetchKpisForVisibleUsers)
  fetchKpisForAllFilteredUsers(
    ctx: StateContext<TimelineKpisStateModel>,
    action: FetchKpisForVisibleUsers,
  ): void {
    const userIds = action.userIds;
    const timeFrameForWeek = this.getTimeFrameForWeek();
    const timeFrameForMonth = this.getTimeFrameForMonth();

    if (timeFrameForWeek) {
      const areTimeFramesDifferent = this.isTimeFramesDifferent(
        this.previousWeekKpiFetchParameters?.timeFrame,
        timeFrameForWeek,
      );
      const newUsersForWeek = this.getNewVisibleUsers(
        this.previousWeekKpiFetchParameters?.userIds || [],
        userIds,
        this.userIdsWithWeekDataLoaded,
      );

      if (newUsersForWeek.length > 0 && areTimeFramesDifferent) {
        // case 1: new users and new time frame --- fetch all
        this.weekKpiFetchRequests$.next({
          userIds,
          timeFrame: timeFrameForWeek,
          ctx,
        });
      } else if (newUsersForWeek.length > 0 && !areTimeFramesDifferent) {
        // case 2: new users and old time frame --- fetch only for new user
        this.weekKpiFetchRequests$.next({
          userIds: newUsersForWeek,
          timeFrame: timeFrameForWeek,
          ctx,
        });
      } else if (newUsersForWeek.length === 0 && areTimeFramesDifferent) {
        // case 3: old users and new time frame --- fetch all users
        this.weekKpiFetchRequests$.next({
          userIds,
          timeFrame: timeFrameForWeek,
          ctx,
        });
      } else if (newUsersForWeek.length === 0 && !areTimeFramesDifferent) {
        // case 4: old users and old time frame --- no op
        // no op
      }

      this.previousWeekKpiFetchParameters = {
        userIds,
        timeFrame: timeFrameForWeek,
      };
    } else {
      console.warn('Warning: Cannot fetch KPIs for week');
      ctx.patchState({
        userKpisForWeek: defaultTimelineKpisState.userKpisForWeek,
        fetchingWeeklyKpiForUserIds: [],
      });
    }

    if (timeFrameForMonth) {
      const newUsersForMonth = this.getNewVisibleUsers(
        this.previousMonthKpiFetchParameters?.userIds || [],
        userIds,
        this.userIdsWithMonthDataLoaded,
      );
      const isMonthTimeFramesDifferent = this.isTimeFramesDifferent(
        this.previousMonthKpiFetchParameters?.timeFrame,
        timeFrameForMonth,
      );

      if (newUsersForMonth.length > 0 && isMonthTimeFramesDifferent) {
        // case 1: new users and new time frame --- fetch all
        this.monthKpiFetchRequests$.next({
          userIds,
          timeFrame: timeFrameForMonth,
          ctx,
        });
      } else if (newUsersForMonth.length > 0 && !isMonthTimeFramesDifferent) {
        // case 2: new users and old time frame --- fetch only for new user
        this.monthKpiFetchRequests$.next({
          userIds: newUsersForMonth,
          timeFrame: timeFrameForMonth,
          ctx,
        });
      } else if (newUsersForMonth.length === 0 && isMonthTimeFramesDifferent) {
        // case 3: old users and new time frame --- fetch all users
        this.monthKpiFetchRequests$.next({
          userIds,
          timeFrame: timeFrameForMonth,
          ctx,
        });
      } else if (newUsersForMonth.length === 0 && !isMonthTimeFramesDifferent) {
        // case 4: old users and old time frame --- no op
        // no op
      }

      this.previousMonthKpiFetchParameters = {
        userIds,
        timeFrame: timeFrameForMonth,
      };
    } else {
      console.warn('Warning: Cannot fetch KPIs for month');
      ctx.patchState({
        userKpisForMonth: defaultTimelineKpisState.userKpisForMonth,
        fetchingMonthlyKpiForUserIds: [],
      });
    }
  }

  @Action(UpdateKpisOfUsers)
  updateKpisOfUsers(
    ctx: StateContext<TimelineKpisStateModel>,
    action: UpdateKpisOfUsers,
  ): void {
    const userIds = action.userIds;
    const timeFrameForWeek = this.getTimeFrameForWeek();
    const timeFrameForMonth = this.getTimeFrameForMonth();

    if (timeFrameForWeek) {
      ctx.patchState({
        updatingKpiUserIds: userIds,
      });
      this.weekKpiUpdateRequests$.next({
        userIds,
        timeFrame: timeFrameForWeek,
        ctx,
      });
    }

    if (timeFrameForMonth) {
      ctx.patchState({
        updatingKpiUserIds: userIds,
      });
      this.monthKpiUpdateRequests$.next({
        userIds,
        timeFrame: timeFrameForMonth,
        ctx,
      });
    }
  }

  private getNewVisibleUsers(
    previousUserIds: string[],
    newUserIds: string[],
    loadedUserIds: string[],
  ): string[] {
    return newUserIds.filter(
      (id) => !previousUserIds.includes(id) || !loadedUserIds.includes(id),
    );
  }

  private isTimeFramesDifferent(
    previousTimeFrame:
      | { startDatetime: string; endDatetime: string }
      | null
      | undefined,
    newTimeFrame: { startDatetime: string; endDatetime: string },
  ): boolean {
    return (
      previousTimeFrame?.startDatetime !== newTimeFrame.startDatetime ||
      previousTimeFrame?.endDatetime !== newTimeFrame.endDatetime
    );
  }

  private handleWeekKpiFetchRequests(): void {
    if (
      !this.weekKpiFetchRequestsSubscription ||
      this.weekKpiFetchRequestsSubscription.closed
    ) {
      this.weekKpiFetchRequestsSubscription = this.weekKpiFetchRequests$
        .pipe(
          debounceTime(500),
          switchMap(({ userIds, timeFrame, ctx }) => {
            const chunks = this.splitIntoChunks(userIds, this.WEEK_CHUNK_SIZE);
            return this.processChunksSequentially(
              chunks,
              (chunk) => {
                ctx.patchState({ fetchingWeeklyKpiForUserIds: chunk });
                return this.workTimeUtilizationsGatewayService.getWorkTimeUtilizations(
                  chunk,
                  {
                    startDatetime: timeFrame.startDatetime,
                    endDatetime: timeFrame.endDatetime,
                  },
                );
              },
              (workTimeUtilizations: WorktimeUtilizationDTO) => {
                const currentState = ctx.getState();
                this.userIdsWithWeekDataLoaded = [
                  ...this.userIdsWithWeekDataLoaded,
                  ...Object.keys(workTimeUtilizations),
                ];
                const updatedKpis = {
                  ...currentState.userKpisForWeek,
                  ...workTimeUtilizations,
                };
                ctx.patchState({
                  userKpisForWeek: updatedKpis,
                  fetchingWeeklyKpiForUserIds: [],
                });
              },
            );
          }),
        )
        .subscribe();
    }
  }

  private handleMonthKpiFetchRequests(): void {
    if (
      !this.monthKpiFetchRequestsSubscription ||
      this.monthKpiFetchRequestsSubscription.closed
    ) {
      this.monthKpiFetchRequestsSubscription = this.monthKpiFetchRequests$
        .pipe(
          debounceTime(500),
          switchMap(({ userIds, timeFrame, ctx }) => {
            const chunks = this.splitIntoChunks(userIds, this.MONTH_CHUNK_SIZE);
            return this.processChunksSequentially(
              chunks,
              (chunk) => {
                ctx.patchState({ fetchingMonthlyKpiForUserIds: chunk });
                return this.workTimeUtilizationsGatewayService.getWorkTimeUtilizations(
                  chunk,
                  {
                    startDatetime: timeFrame.startDatetime,
                    endDatetime: timeFrame.endDatetime,
                  },
                );
              },
              (workTimeUtilizations: WorktimeUtilizationDTO) => {
                const currentState = ctx.getState();
                this.userIdsWithMonthDataLoaded = [
                  ...this.userIdsWithMonthDataLoaded,
                  ...Object.keys(workTimeUtilizations),
                ];
                const updatedKpis = {
                  ...currentState.userKpisForMonth,
                  ...workTimeUtilizations,
                };
                ctx.patchState({
                  userKpisForMonth: updatedKpis,
                  fetchingMonthlyKpiForUserIds: [],
                });
              },
            );
          }),
        )
        .subscribe();
    }
  }

  private handleWeekKpiUpdateRequests(): void {
    if (
      !this.weekKpiUpdateRequestsSubscription ||
      this.weekKpiUpdateRequestsSubscription.closed
    ) {
      this.weekKpiUpdateRequestsSubscription = this.weekKpiUpdateRequests$
        .pipe(
          switchMap(({ userIds, timeFrame, ctx }) => {
            const chunks = this.splitIntoChunks(userIds, this.WEEK_CHUNK_SIZE);
            return this.processChunksSequentially(
              chunks,
              (chunk) =>
                this.workTimeUtilizationsGatewayService.getWorkTimeUtilizations(
                  chunk,
                  {
                    startDatetime: timeFrame.startDatetime,
                    endDatetime: timeFrame.endDatetime,
                  },
                ),
              (workTimeUtilizations: WorktimeUtilizationDTO) => {
                const currentState = ctx.getState();
                const updatedKpis = {
                  ...currentState.userKpisForWeek,
                  ...workTimeUtilizations,
                };
                const remainingLoadingIds =
                  currentState.updatingKpiUserIds.filter(
                    (id) => !Object.keys(workTimeUtilizations).includes(id),
                  );
                ctx.patchState({
                  userKpisForWeek: updatedKpis,
                  updatingKpiUserIds: remainingLoadingIds,
                });
              },
            );
          }),
        )
        .subscribe();
    }
  }

  private handleMonthKpiUpdateRequests(): void {
    if (
      !this.monthKpiUpdateRequestsSubscription ||
      this.monthKpiUpdateRequestsSubscription.closed
    ) {
      this.monthKpiUpdateRequestsSubscription = this.monthKpiUpdateRequests$
        .pipe(
          switchMap(({ userIds, timeFrame, ctx }) => {
            const chunks = this.splitIntoChunks(userIds, this.MONTH_CHUNK_SIZE);
            return this.processChunksSequentially(
              chunks,
              (chunk) =>
                this.workTimeUtilizationsGatewayService.getWorkTimeUtilizations(
                  chunk,
                  {
                    startDatetime: timeFrame.startDatetime,
                    endDatetime: timeFrame.endDatetime,
                  },
                ),
              (workTimeUtilizations: WorktimeUtilizationDTO) => {
                const currentState = ctx.getState();
                const updatedKpis = {
                  ...currentState.userKpisForMonth,
                  ...workTimeUtilizations,
                };
                const remainingLoadingIds =
                  currentState.updatingKpiUserIds.filter(
                    (id) => !Object.keys(workTimeUtilizations).includes(id),
                  );
                ctx.patchState({
                  userKpisForMonth: updatedKpis,
                  updatingKpiUserIds: remainingLoadingIds,
                });
              },
            );
          }),
        )
        .subscribe();
    }
  }

  private getTimeFrameForWeek(): {
    startDatetime: string;
    endDatetime: string;
  } | null {
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId) {
      const isWorkTimeWeekKpiVisible = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.showWorkTimeWeekKpi(userId),
      );
      const isPlannedTimeWeekKpiVisible = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.showPlannedTimeWeekKpi(userId),
      );
      const isOvertimeWeekKpiVisible = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.showOvertimeWeekKpi(userId),
      );
      if (
        isWorkTimeWeekKpiVisible ||
        isPlannedTimeWeekKpiVisible ||
        isOvertimeWeekKpiVisible
      ) {
        const timeframe = this.store.selectSnapshot(
          ShiftTimelineDataState.visibleDateRange,
        );
        const weekStart = startOfWeek(timeframe.start, {
          weekStartsOn: 1,
        });
        const weekEnd = endOfWeek(timeframe.start, {
          weekStartsOn: 1,
        });
        return {
          startDatetime: format(weekStart, DATABASE_DATE_TIME_FORMAT),
          endDatetime: format(weekEnd, DATABASE_DATE_TIME_FORMAT),
        };
      }
    }
    return null;
  }

  private getTimeFrameForMonth(): {
    startDatetime: string;
    endDatetime: string;
  } | null {
    const userId = this.store.selectSnapshot(AuthState.userId);
    if (userId) {
      const isWorkTimeMonthKpiVisible = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.showWorkTimeMonthKpi(userId),
      );
      const isPlannedTimeMonthKpiVisible = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.showPlannedTimeMonthKpi(userId),
      );
      const isOvertimeMonthKpiVisible = this.store.selectSnapshot(
        ShiftTimelinePreferredSettingsState.showOvertimeMonthKpi(userId),
      );
      if (
        isWorkTimeMonthKpiVisible ||
        isPlannedTimeMonthKpiVisible ||
        isOvertimeMonthKpiVisible
      ) {
        const timeframe = this.store.selectSnapshot(
          ShiftTimelineDataState.visibleDateRange,
        );

        const monthStart = startOfMonth(timeframe.start);
        const monthEnd = endOfMonth(timeframe.start);
        return {
          startDatetime: format(monthStart, DATABASE_DATE_TIME_FORMAT),
          endDatetime: format(monthEnd, DATABASE_DATE_TIME_FORMAT),
        };
      }
    }
    return null;
  }
}
